import { create } from 'zustand';

export type ThemeMode = 'light' | 'dark';

const resolveStoredTheme = (): ThemeMode | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  const stored = window.localStorage.getItem('shell-theme');
  return stored === 'light' || stored === 'dark' ? stored : null;
};

const resolveInitialTheme = (): ThemeMode => {
  if (typeof window === 'undefined') {
    return 'dark';
  }

  const stored = resolveStoredTheme();
  if (stored) {
    return stored;
  }

  const attrTheme = document.documentElement.getAttribute('data-theme');
  if (attrTheme === 'light' || attrTheme === 'dark') {
    return attrTheme;
  }

  if (window.matchMedia?.('(prefers-color-scheme: light)').matches) {
    return 'light';
  }

  return 'dark';
};

const applyTheme = (theme: ThemeMode) => {
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-theme', theme);
  }

  if (typeof window !== 'undefined') {
    window.localStorage.setItem('shell-theme', theme);
    window.dispatchEvent(new CustomEvent<ThemeMode>('shell-theme-change', { detail: theme }));
  }
};

interface ThemeStore {
  theme: ThemeMode;
  setTheme: (theme: ThemeMode) => void;
  toggleTheme: () => void;
  syncFromStorage: () => void;
}

export const useThemeStore = create<ThemeStore>((set, get) => ({
  theme: resolveInitialTheme(),

  setTheme: (theme) => {
    applyTheme(theme);
    set({ theme });
  },

  toggleTheme: () => {
    const next = get().theme === 'dark' ? 'light' : 'dark';
    applyTheme(next);
    set({ theme: next });
  },

  syncFromStorage: () => {
    const next = resolveInitialTheme();
    if (next !== get().theme) {
      applyTheme(next);
      set({ theme: next });
    }
  },
}));

if (typeof window !== 'undefined') {
  applyTheme(useThemeStore.getState().theme);

  window.addEventListener('storage', (event) => {
    if (event.key === 'shell-theme') {
      useThemeStore.getState().syncFromStorage();
    }
  });
}

export default useThemeStore;
